import {Container, ContainerModule, type interfaces} from "inversify";
import {Bindings} from "data/constants/bindings";
import {type IUserStore, UserStore} from "data/stores/user/user.store";
import {AuthController, type IAuthController} from "views/controllers/auth/auth.controller";
import {API_URL, CONTENT_JSON_URL, JSON_URL} from "data/constants";
import {type IJSONProvider, JSONProvider} from "data/providers/json/json.provider";
import {AuthApiProvider, type IAuthApiProvider} from "data/providers/api/auth.api.provider";
import {HttpClientFactory, type IHttpClientService} from "data/services/http";
import {PlayersStore, type IPlayersStore} from "data/stores/players/players.store";
import {SquadsStore, type ISquadsStore} from "data/stores/squads/squads.store";
import {
	type ISecretGateController,
	SecretGateController,
} from "views/controllers/secret_gate/secret_gate.controller";
import {
	type ILocalizationStore,
	LocalizationStore,
} from "data/stores/localization/localization.store";
import {
	type IStaticContentStore,
	StaticContentStore,
} from "data/stores/static_content/static_content.store";
import {HelpController, type IHelpController} from "views/pages/help/help.controller";
import {
	HelpListController,
	type IHelpListController,
} from "views/components/help/help_list/help_list.controller";
import {
	ContactUsController,
	type IContactUsController,
} from "views/components/help/contact_us/contact_us.controller";
import {
	type ILiveScoreController,
	LiveScoreController,
} from "views/controllers/live_score/live_score.controller";
import {ChecksumStore, type IChecksumStore} from "data/stores/checksum/checksum.store";
import {type IUserApiProvider, UserApiProvider} from "data/providers/api/user.api.provider";
import {
	type IPasswordApiProvider,
	PasswordApiProvider,
} from "data/providers/api/password.api.provider";
import {
	type ISessionController,
	SessionController,
} from "views/components/session/session.controller";
import {
	BootstrapController,
	type IBootstrapController,
} from "views/components/bootstrap/bootstrap.controller";
import {
	type ILeaguesApiProvider,
	LeaguesApiProvider,
} from "data/providers/api/leagues.api.provider";
import {type ILeaguesStore, LeaguesStore} from "data/stores/leagues/leagues.store";
import {
	type ILeagueInviteFormController,
	LeagueInviteFormController,
} from "views/components/league_invite_form/league_invite_form.controller";
import {type IModalsStore, ModalsStore} from "data/stores/modals/modals.store";
import {
	type IModalErrorController,
	ModalErrorController,
} from "views/components/modals/modal_error/modal_error.controller";
import {
	type IModalConfirmController,
	ModalConfirmController,
} from "views/components/modals/modal_confirm/modal_confirm.controller";
import {
	type ILocalizationController,
	LocalizationController,
} from "views/controllers/localization/localization.controller";
import {
	type IBackdoorController,
	BackdoorController,
} from "views/pages/backdoor/backdoor.controller";
import {type IHeaderController, HeaderController} from "views/components/header/header.controller";
import {type IMenuStore, MenuStore} from "data/stores/menu/menu.store";
import {
	type IMenuToggleController,
	MenuToggleController,
} from "views/components/menu/menu_toggle/menu_toggle.controller";
import {type IContestsStore, ContestsStore} from "data/stores/contests/contests.store";
import {type IGamesStore, GamesStore} from "data/stores/games/games.store";
import {
	type IContestPlayersStore,
	ContestPlayersStore,
} from "data/stores/contest_players/contest_players.store";
import {
	type IPredictionApiProvider,
	PredictionApiProvider,
} from "data/providers/api/predictions.api.provider";
import {type IPredictionsStore, PredictionsStore} from "data/stores/predictions/predictions.store";
import {HomeController, type IHomeController} from "views/pages/home/home.controller";
import {type IPlayersController, PlayersController} from "views/pages/players/players.controller";
import {
	type IContestCardController,
	ContestCardController,
} from "views/components/contest_card/contest_card.controller";
import {
	type IPlayerCardController,
	PlayerCardController,
} from "views/components/player_card/player_card.controller";
import {
	type IModalSecondaryRegistrationController,
	ModalSecondaryRegistrationController,
} from "views/components/modals/modal_secondary_registration/modal_secondary_registration.controller";
import {
	type IContestInfoController,
	ContestInfoController,
} from "views/components/contest_info/contest_info.controller";
import {
	type ISaveBarController,
	SaveBarController,
} from "views/components/save_bar/save_bar.controller";
import {
	type IWinnerCardController,
	WinnerCardController,
} from "views/components/winner_card/winner_card.controller";
import {
	type IModalSaveLocalController,
	ModalSaveLocalController,
} from "views/components/modals/modal_save_local_prediction/modal_save_local_prediction.controller";
import {
	type ILivePlayersController,
	LivePlayersController,
} from "views/components/live_players/live_players.controller";
import {
	type ILeaderboardController,
	LeaderboardController,
} from "views/pages/leaderboard/leaderboard.controller";
import {
	type IRankingApiProvider,
	RankingApiProvider,
} from "data/providers/api/ranking.api.provider";
import {type IRankingStore, RankingStore} from "data/stores/ranking/ranking.store";
import {
	type ILeaderboardTableController,
	LeaderboardTableController,
} from "views/components/leaderboard_table/leaderboard_table.controller";
import {
	type ILeaderboardRowController,
	LeaderboardRowController,
} from "views/components/leaderboard_row/leaderboard_row.controller";
import {type IRoundsStore, RoundsStore} from "data/stores/rounds/rounds.store";

export const DIContainer = new Container();

export const services = new ContainerModule((bind: interfaces.Bind) => {
	bind<IHttpClientService>(Bindings.ApiHTTPClient).toConstantValue(
		HttpClientFactory.createApiClient({
			baseURL: API_URL,
			withCredentials: true,
		})
	);

	bind<IHttpClientService>(Bindings.JsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: JSON_URL,
		})
	);

	bind<IHttpClientService>(Bindings.ContentJsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: CONTENT_JSON_URL,
		})
	);
});

export const providers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IJSONProvider>(Bindings.JSONProvider).to(JSONProvider);
	bind<IAuthApiProvider>(Bindings.AuthApiProvider).to(AuthApiProvider);
	bind<IUserApiProvider>(Bindings.UserApiProvider).to(UserApiProvider);
	bind<IPasswordApiProvider>(Bindings.PasswordApiProvider).to(PasswordApiProvider);
	bind<ILeaguesApiProvider>(Bindings.LeaguesApiProvider).to(LeaguesApiProvider);
	bind<IPredictionApiProvider>(Bindings.PredictionApiProvider).to(PredictionApiProvider);
	bind<IRankingApiProvider>(Bindings.RankingApiProvider).to(RankingApiProvider);
});

export const stores = new ContainerModule((bind: interfaces.Bind) => {
	bind<ILocalizationStore>(Bindings.LocalizationStore).to(LocalizationStore).inSingletonScope();
	bind<IUserStore>(Bindings.UserStore).to(UserStore).inSingletonScope();
	bind<IPlayersStore>(Bindings.PlayersStore).to(PlayersStore).inSingletonScope();
	bind<ISquadsStore>(Bindings.SquadsStore).to(SquadsStore).inSingletonScope();
	bind<IStaticContentStore>(Bindings.StaticContentStore).to(StaticContentStore);

	bind<IChecksumStore>(Bindings.ChecksumStore).to(ChecksumStore);
	bind<ILeaguesStore>(Bindings.LeaguesStore).to(LeaguesStore).inSingletonScope();
	bind<IModalsStore>(Bindings.ModalsStore).to(ModalsStore).inSingletonScope();
	bind<IContestsStore>(Bindings.ContestsStore).to(ContestsStore).inSingletonScope();
	bind<IMenuStore>(Bindings.MenuStore).to(MenuStore).inSingletonScope();
	bind<IGamesStore>(Bindings.GamesStore).to(GamesStore).inSingletonScope();
	bind<IContestPlayersStore>(Bindings.ContestPlayersStore)
		.to(ContestPlayersStore)
		.inSingletonScope();
	bind<IPredictionsStore>(Bindings.PredictionsStore).to(PredictionsStore).inSingletonScope();
	bind<IRankingStore>(Bindings.RankingStore).to(RankingStore).inSingletonScope();
	bind<IRoundsStore>(Bindings.RoundsStore).to(RoundsStore).inSingletonScope();
});

export const controllers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IAuthController>(Bindings.AuthController).to(AuthController);
	bind<ISecretGateController>(Bindings.SecretGateController).to(SecretGateController);
	bind<IHelpController>(Bindings.HelpController).to(HelpController);
	bind<IHelpListController>(Bindings.HelpListController).to(HelpListController);
	bind<IContactUsController>(Bindings.ContactUsController).to(ContactUsController);
	bind<ILiveScoreController>(Bindings.LiveScoreController).to(LiveScoreController);
	bind<ISessionController>(Bindings.SessionController).to(SessionController);
	bind<IBootstrapController>(Bindings.BootstrapController).to(BootstrapController);
	bind<ILeagueInviteFormController>(Bindings.LeagueInviteFormController).to(
		LeagueInviteFormController
	);
	bind<IModalErrorController>(Bindings.ModalErrorController).to(ModalErrorController);
	bind<IModalConfirmController>(Bindings.ModalConfirmController).to(ModalConfirmController);
	bind<ILocalizationController>(Bindings.LocalizationController).to(LocalizationController);
	bind<IBackdoorController>(Bindings.BackdoorController).to(BackdoorController);
	bind<IHeaderController>(Bindings.HeaderController).to(HeaderController);
	bind<IMenuToggleController>(Bindings.MenuToggleController).to(MenuToggleController);
	bind<IHomeController>(Bindings.HomeController).to(HomeController);
	bind<IPlayersController>(Bindings.PlayersController).to(PlayersController);
	bind<IContestCardController>(Bindings.ContestCardController).to(ContestCardController);
	bind<IPlayerCardController>(Bindings.PlayerCardController).to(PlayerCardController);
	bind<IModalSecondaryRegistrationController>(Bindings.ModalSecondaryRegistrationController).to(
		ModalSecondaryRegistrationController
	);
	bind<IContestInfoController>(Bindings.ContestInfoController).to(ContestInfoController);
	bind<ISaveBarController>(Bindings.SaveBarController).to(SaveBarController);
	bind<IWinnerCardController>(Bindings.WinnerCardController).to(WinnerCardController);
	bind<IModalSaveLocalController>(Bindings.ModalSaveLocalController).to(ModalSaveLocalController);
	bind<ILivePlayersController>(Bindings.LivePlayersController).to(LivePlayersController);
	bind<ILeaderboardController>(Bindings.LeaderboardController).to(LeaderboardController);
	bind<ILeaderboardTableController>(Bindings.LeaderboardTableController).to(
		LeaderboardTableController
	);
	bind<ILeaderboardRowController>(Bindings.LeaderboardRowController).to(LeaderboardRowController);
});
